// The auto-texting suggestion engine (Texting Center Phase 1: drips).
// Spec: docs/superpowers/specs/2026-07-19-auto-texting-engine-design.md
//
// Pure rule functions compute which suggestions SHOULD exist for a lead;
// syncTextJobs materializes them as textJobs docs (deterministic ids make it
// idempotent) and cancels the ones whose reason disappeared. NOTHING in this
// file sends a text: jobs wait in the Texting Center until a human approves.
// Henry's rule: no em dashes in any client-facing copy.
import { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { renderTemplate, firstNameOf } from "./texting";
import { parseLocalDate } from "./statusSheetData";

const DAY_MS = 86400000;
export const JOB_EXPIRY_MS = 14 * DAY_MS;
export const DRIP_EARLY_STAGES = ["New Lead", "Nurturing"];

export const DEFAULT_DRIP_STEPS = [
  {
    day: 1,
    template:
      "Hi {firstName}! Great connecting. Want me to line up a few homes to look at this week?",
  },
  {
    day: 3,
    template:
      "Hi {firstName}, a couple of new listings just hit that might fit what you're looking for. Want me to send them over?",
  },
  {
    day: 7,
    template:
      "Hi {firstName}, just checking in! Any questions I can answer about the market or next steps?",
  },
  {
    day: 14,
    template:
      "Hi {firstName}, still here for you whenever you're ready. Want to set up a quick call this week?",
  },
  {
    day: 30,
    template:
      "Hi {firstName}, it's been a few weeks. Still planning to make a move this year? Happy to help whenever the timing works.",
  },
];

export const dripJobId = (leadId, day) => `drip__${leadId}__d${day}`;

// All drip suggestions that SHOULD exist for this lead right now. Pure.
export function dripJobDrafts(lead, settings, now) {
  if (!settings?.dripEnabled) return [];
  if (!lead || lead.status !== "active" || lead.doNotText) return [];
  if (!DRIP_EARLY_STAGES.includes(lead.stage)) return [];
  if (!lead.contactId || !lead.createdAt) return [];

  const steps =
    Array.isArray(settings.dripSteps) && settings.dripSteps.length
      ? settings.dripSteps
      : DEFAULT_DRIP_STEPS;

  const drafts = [];
  for (const step of steps) {
    const day = Number(step?.day);
    if (!Number.isFinite(day) || day <= 0) continue;
    const dueAt = lead.createdAt + day * DAY_MS;
    if (now - dueAt > JOB_EXPIRY_MS) continue; // long past due: don't resurrect
    const body = renderTemplate(step.template, {
      firstName: firstNameOf(lead.contactName),
      agentName: lead.assignedAgentName || "",
    }).trim();
    if (!body) continue;
    drafts.push({
      id: dripJobId(lead.id, day),
      ruleKey: "drip",
      step: `d${day}`,
      leadId: lead.id,
      contactId: lead.contactId,
      contactName: lead.contactName || "",
      dueAt,
      body,
    });
  }
  return drafts;
}

// Why a pending drip job should stop existing (string reason), or null. Pure.
export function shouldCancelDrip(lead, lastInboundAt, job, now) {
  if (!lead) return "lead-gone";
  if (lead.status !== "active")
    return lead.status === "converted" ? "converted" : "inactive";
  if (lead.doNotText) return "do-not-text";
  if (!DRIP_EARLY_STAGES.includes(lead.stage)) return "stage-advanced";
  if (String(lead.appointmentAt || "").trim()) return "appointment-set";
  if (lastInboundAt && lastInboundAt > (job.createdAt || 0)) return "replied";
  if (now > (job.dueAt || 0) + JOB_EXPIRY_MS) return "expired";
  return null;
}

// ---- Phase 2 rules: reminders + past-client touches ----

const fmtShortDate = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const atHour = (baseMs, hour) => {
  const d = new Date(baseMs);
  d.setHours(hour, 0, 0, 0);
  return d.getTime();
};
const dateKey = (str) => String(str || "").replace(/\W+/g, "-");

// Appointment reminders: day-before at 4pm + morning-of at 8am. Pure.
export function apptReminderDrafts(lead, settings, now) {
  if (!settings?.apptReminderEnabled) return [];
  if (!lead || lead.status !== "active" || lead.doNotText || !lead.contactId)
    return [];
  const appt = parseLocalDate(lead.appointmentAt);
  if (!appt) return [];
  if (now > atHour(appt.getTime(), 23)) return []; // appointment already past
  const key = dateKey(lead.appointmentAt);
  const mk = (step, dueAt, apptDateLabel) => {
    if (now - dueAt > JOB_EXPIRY_MS) return null;
    const body = renderTemplate(settings.apptReminderTemplate, {
      firstName: firstNameOf(lead.contactName) || "there",
      apptDate: apptDateLabel,
    }).trim();
    if (!body) return null;
    return {
      id: `apptReminder__${lead.id}__${key}__${step}`,
      ruleKey: "apptReminder",
      step,
      leadId: lead.id,
      contactId: lead.contactId,
      contactName: lead.contactName || "",
      dueAt,
      body,
    };
  };
  return [
    mk("pre", atHour(appt.getTime() - 86400000, 16), `tomorrow (${fmtShortDate(appt)})`),
    mk("day", atHour(appt.getTime(), 8), `today (${fmtShortDate(appt)})`),
  ].filter(Boolean);
}

const dealIdOf = (deal) => deal?.id || deal?.createdat || "";
const dealAddress = (deal) =>
  deal?.propertyaddress || deal?.propertyName || deal?.address || "your home";
const dealContact = (deal) =>
  Array.isArray(deal?.customers) ? deal.customers[0] || "" : "";

// Closing countdown: 1 week out at 9am + day-before at 4pm. Pure.
export function closingReminderDrafts(deal, settings, now) {
  if (!settings?.closingReminderEnabled) return [];
  const dealId = dealIdOf(deal);
  if (!deal || !dealId || deal.doNotText || !dealContact(deal)) return [];
  if (["Cancelled", "Expired"].includes(deal.dealstage)) return [];
  const close = parseLocalDate(deal.closingdate);
  if (!close) return [];
  if (now > atHour(close.getTime(), 23)) return [];
  const key = dateKey(deal.closingdate);
  const mk = (step, dueAt) => {
    if (now - dueAt > JOB_EXPIRY_MS) return null;
    const body = renderTemplate(settings.closingReminderTemplate, {
      firstName: "there",
      address: dealAddress(deal),
      closingDate: fmtShortDate(close),
    }).trim();
    if (!body) return null;
    return {
      id: `closingReminder__${dealId}__${key}__${step}`,
      ruleKey: "closingReminder",
      step,
      dealId,
      contactId: dealContact(deal),
      contactName: deal.clientLastName || "",
      dueAt,
      body,
    };
  };
  return [
    mk("wk", atHour(close.getTime() - 7 * 86400000, 9)),
    mk("day", atHour(close.getTime() - 86400000, 16)),
  ].filter(Boolean);
}

// Review ask 3 days after closing (needs the Google review link in Settings).
export function reviewRequestDrafts(deal, settings, now) {
  if (!settings?.reviewRequestEnabled || !settings?.reviewLink) return [];
  const dealId = dealIdOf(deal);
  if (!deal || !dealId || deal.doNotText || !dealContact(deal)) return [];
  if (["Cancelled", "Expired"].includes(deal.dealstage)) return [];
  const close = parseLocalDate(deal.closingdate);
  if (!close) return [];
  const dueAt = atHour(close.getTime() + 3 * 86400000, 10);
  if (now - dueAt > JOB_EXPIRY_MS) return [];
  const body = renderTemplate(settings.reviewRequestTemplate, {
    firstName: "there",
    address: dealAddress(deal),
    reviewLink: settings.reviewLink,
  }).trim();
  if (!body) return [];
  return [
    {
      id: `reviewRequest__${dealId}__${dateKey(deal.closingdate)}`,
      ruleKey: "reviewRequest",
      step: "d3",
      dealId,
      contactId: dealContact(deal),
      contactName: deal.clientLastName || "",
      dueAt,
      body,
    },
  ];
}

// Happy-one-year text, 365 days after closing.
export function anniversaryDrafts(deal, settings, now) {
  if (!settings?.anniversaryEnabled) return [];
  const dealId = dealIdOf(deal);
  if (!deal || !dealId || deal.doNotText || !dealContact(deal)) return [];
  if (["Cancelled", "Expired"].includes(deal.dealstage)) return [];
  const close = parseLocalDate(deal.closingdate);
  if (!close) return [];
  const dueAt = atHour(close.getTime() + 365 * 86400000, 10);
  if (now - dueAt > JOB_EXPIRY_MS) return [];
  const body = renderTemplate(settings.anniversaryTemplate, {
    firstName: "there",
    address: dealAddress(deal),
  }).trim();
  if (!body) return [];
  return [
    {
      id: `anniversary__${dealId}__${dateKey(deal.closingdate)}`,
      ruleKey: "anniversary",
      step: "y1",
      dealId,
      contactId: dealContact(deal),
      contactName: deal.clientLastName || "",
      dueAt,
      body,
    },
  ];
}

// Everything that SHOULD exist right now, across every rule. Pure.
export function allJobDrafts({ leads, deals, settings, now }) {
  const drafts = [];
  (leads || []).forEach((lead) => {
    drafts.push(...dripJobDrafts(lead, settings, now));
    drafts.push(...apptReminderDrafts(lead, settings, now));
  });
  (deals || []).forEach((deal) => {
    drafts.push(...closingReminderDrafts(deal, settings, now));
    drafts.push(...reviewRequestDrafts(deal, settings, now));
    drafts.push(...anniversaryDrafts(deal, settings, now));
  });
  return drafts;
}

const MANAGED_RULES = [
  "drip",
  "apptReminder",
  "closingReminder",
  "reviewRequest",
  "anniversary",
  "aiReply",
];

// ---- Firestore sync (idempotent by construction) ----

// Materialize missing suggestions and cancel obsolete ones. Safe to run on
// every Texting Center load: deterministic ids mean re-runs write nothing new.
export async function syncTextJobs({ leads, deals, settings }) {
  const now = Date.now();
  const result = { created: 0, cancelled: 0 };
  try {
    const leadsById = {};
    (leads || []).forEach((l) => {
      if (l?.id) leadsById[l.id] = l;
    });

    // Latest inbound/outbound per lead (for "replied" cancellations).
    const msgSnap = await getDocs(collection(firestored, "textMessages"));
    const lastInboundByLead = {};
    const lastOutboundByLead = {};
    msgSnap.docs.forEach((d) => {
      const m = d.data();
      if (!m.leadId) return;
      const bucket =
        m.direction === "inbound" ? lastInboundByLead : lastOutboundByLead;
      bucket[m.leadId] = Math.max(bucket[m.leadId] || 0, m.createdAt || 0);
    });

    // EVERY job ever created, any status. Creation must check this full set:
    // a skipped/sent/cancelled job counts as handled forever, otherwise a
    // re-run would resurrect it (the "skipped texts come back" bug).
    const allSnap = await getDocs(collection(firestored, "textJobs"));
    const everById = {};
    const pendingById = {};
    allSnap.docs.forEach((d) => {
      const job = { id: d.id, ...d.data() };
      everById[d.id] = job;
      if (job.status === "suggested") pendingById[d.id] = job;
    });

    // Create what's missing (every rule, every record). Checked against
    // EVERY job ever made, not just pending ones: skipped/sent/cancelled
    // jobs are handled forever and must never be resurrected.
    const desired = allJobDrafts({ leads, deals, settings, now });
    const desiredById = {};
    for (const draft of desired) {
      desiredById[draft.id] = draft;
      if (everById[draft.id]) continue;
      await setDoc(
        doc(firestored, "textJobs", draft.id),
        { ...draft, status: "suggested", createdAt: now },
        { merge: true },
      );
      result.created += 1;
    }

    // Cancel what no longer makes sense.
    for (const job of Object.values(pendingById)) {
      if (!MANAGED_RULES.includes(job.ruleKey)) continue;
      let reason = null;
      if (job.ruleKey === "aiReply") {
        // An AI draft dies the moment anyone actually replied, or goes stale.
        if ((lastOutboundByLead[job.leadId] || 0) > (job.createdAt || 0))
          reason = "replied";
        else if (now > (job.dueAt || 0) + JOB_EXPIRY_MS) reason = "expired";
      } else if (job.ruleKey === "drip") {
        reason = !settings?.dripEnabled
          ? "disabled"
          : shouldCancelDrip(
              leadsById[job.leadId] || null,
              lastInboundByLead[job.leadId] || 0,
              job,
              now,
            );
      } else if (!desiredById[job.id]) {
        // Date moved, switch turned off, record advanced: no longer applies.
        reason = "no-longer-applies";
      } else if (now > (job.dueAt || 0) + JOB_EXPIRY_MS) {
        reason = "expired";
      }
      if (reason) {
        await setDoc(
          doc(firestored, "textJobs", job.id),
          { status: "cancelled", cancelReason: reason, resolvedAt: now },
          { merge: true },
        );
        result.cancelled += 1;
      }
    }
  } catch (e) {
    console.error("syncTextJobs failed:", e);
  }
  return result;
}

// Resolve a suggestion: sent, skipped, or cancelled.
export async function resolveTextJob(jobId, patch) {
  await setDoc(
    doc(firestored, "textJobs", jobId),
    { ...patch, resolvedAt: Date.now() },
    { merge: true },
  );
}

// ---- Shared live view of pending suggestions (useUnreadTexts pattern) ----

let jobsCache = [];
const jobSubscribers = new Set();
let jobsUnsub = null;

function ensureJobsListener() {
  if (jobsUnsub) return;
  const q = query(
    collection(firestored, "textJobs"),
    where("status", "==", "suggested"),
  );
  jobsUnsub = onSnapshot(
    q,
    (snap) => {
      jobsCache = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.dueAt || 0) - (b.dueAt || 0));
      jobSubscribers.forEach((fn) => fn(jobsCache));
    },
    (err) => console.error("textJobs listener failed:", err),
  );
}

// All pending suggestions, oldest due first. Callers filter dueAt <= now.
export function useSuggestedTextJobs(enabled = true) {
  const [jobs, setJobs] = useState(jobsCache);
  useEffect(() => {
    if (!enabled) return undefined;
    ensureJobsListener();
    jobSubscribers.add(setJobs);
    setJobs(jobsCache);
    return () => jobSubscribers.delete(setJobs);
  }, [enabled]);
  return jobs;
}
